/** Browser text-to-speech helpers for Training learner content. */

export function stripMarkdownForSpeech(text) {
  return String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\|/g, ' ')
    .replace(/[-*]{3,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isSpeechSupported() {
  return typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined';
}

export function stopSpeaking() {
  if (!isSpeechSupported()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    // ignore
  }
}

/**
 * Speak plain or markdown text. Cancels any in-progress utterance first.
 * Returns a cleanup function that stops speech and clears listeners.
 */
export function speakText(text, { rate = 1, pitch = 1, lang = 'en-US', onStart, onEnd, onError } = {}) {
  const plain = stripMarkdownForSpeech(text);
  if (!plain || !isSpeechSupported()) {
    onError?.(new Error(isSpeechSupported() ? 'Nothing to read' : 'Speech not supported'));
    return () => {};
  }

  stopSpeaking();
  const utterance = new SpeechSynthesisUtterance(plain);
  utterance.rate = rate;
  utterance.pitch = pitch;
  utterance.lang = lang;

  const voices = window.speechSynthesis.getVoices?.() || [];
  const preferred = voices.find((v) => v.lang?.startsWith('en') && /google|Samantha|Daniel|Natural/i.test(v.name))
    || voices.find((v) => v.lang?.startsWith('en'));
  if (preferred) utterance.voice = preferred;

  utterance.onstart = () => onStart?.();
  utterance.onend = () => onEnd?.();
  utterance.onerror = (event) => {
    if (event?.error === 'interrupted' || event?.error === 'canceled') {
      onEnd?.();
      return;
    }
    onError?.(event);
    onEnd?.();
  };

  // Chrome sometimes needs a tick after cancel before speak works reliably
  window.setTimeout(() => {
    try {
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      onError?.(err);
      onEnd?.();
    }
  }, 0);

  return () => {
    stopSpeaking();
  };
}
